import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { debounce, isNumber } from "lodash";
import Autocomplete from "@mui/material/Autocomplete";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import StoreIcon from "@mui/icons-material/StoreTwoTone";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  createEntity,
  entitySelector,
  saveValue,
  userDetailsSelector,
} from "../store/slices/applicationFormSlice";
import { getBusinessByABN, getBusinessByName } from "../services/abr";
import readableABNString from "../utils/readableABNString";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Paper,
  Skeleton,
} from "@mui/material";
import Entity from "./Entity";

export default function Entities({ application }) {
  const dispatch = useDispatch();
  const [confirmRemoveEntity, setConfirmRemoveEntity] = useState({});
  const { gettingEntityDetails } = useSelector(userDetailsSelector);

  const entity = useSelector(entitySelector);
  const [businessSearchValue, setBusinessSearchValue] = useState("");
  const [businessSearchInputText, setBusinessSearchInputText] = useState("");
  const [loadingBusinessSearch, setLoadingBusinessSearch] = useState("");
  const [entityOptions, setEntityOptions] = useState([]);

  const debounced = debounce(async (inputValue) => {
    if (inputValue) {
      setLoadingBusinessSearch(true);

      if (isNumber(inputValue.replace(/\s/g, "") * 1)) {
        const businessByABN = await getBusinessByABN(
          inputValue.replace(/\s/g, "") * 1
        );
        setLoadingBusinessSearch(false);
        if (businessByABN.Abn.length > 1) {
          return setEntityOptions([businessByABN]);
        }
      }

      const businesses = await getBusinessByName(inputValue);
      if (businesses?.Names?.length > 0) {
        setLoadingBusinessSearch(false);
        return setEntityOptions(
          businesses.Names.map((i) => {
            // return { label: `${i.Name} ${i.Abn}`, Abn: i.Abn, value: i };
            return i;
          })
        );
      }

      setEntityOptions([
        {
          Name: "No results found",
        },
      ]);
    }
  }, 500);

  const handleEntityDetails = async (details) => {
    dispatch(saveValue({ gettingEntityDetails: true }));
    const abrData = await getBusinessByABN(details.Abn);

    if (abrData.EntityName !== entity?.entityName) {
      // await dispatch(getCompanyProfile({ ACN: abrData.Acn })).unwrap();
      dispatch(createEntity({ abrData, applicationId: application._id }));
    } else {
      dispatch(saveValue({ gettingEntityDetails: false }));
    }
  };

  useEffect(() => {
    debounced(businessSearchInputText);

    return () => debounced.cancel();
  }, [businessSearchValue, businessSearchInputText]);

  useEffect(() => {
    if (!gettingEntityDetails) {
      setBusinessSearchInputText("");
      setBusinessSearchValue("");
    }
  }, [gettingEntityDetails]);

  return (
    <>
      {application.entities &&
        application.entities.map((entity) => (
          <Entity
            key={entity._id}
            applicationId={application._id}
            entity={entity}
            confirmRemoveEntity={confirmRemoveEntity}
            setConfirmRemoveEntity={setConfirmRemoveEntity}
          />
        ))}

      {gettingEntityDetails && (
        <Accordion defaultExpanded={true} expanded={true}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <StoreIcon style={{ marginRight: "15px" }} />
            <Typography>Getting entity details...</Typography>
          </AccordionSummary>
          <AccordionDetails style={{ padding: "10px 36px 36px" }}>
            <Grid item container spacing={2} style={{ marginBottom: "15px" }}>
              <Grid item sm={4}>
                <Skeleton variant="text" />
                <Skeleton variant="text" />
              </Grid>
              <Grid item sm={4}>
                <Skeleton variant="text" />
                <Skeleton variant="text" />
              </Grid>
              <Grid item sm={4}>
                <Skeleton variant="text" />
                <Skeleton variant="text" />
              </Grid>
            </Grid>
            <Grid item container spacing={2} style={{ marginBottom: "15px" }}>
              <Grid item sm={4}>
                <Skeleton variant="text" />
                <Skeleton variant="text" />
              </Grid>
              <Grid item sm={4}>
                <Skeleton variant="text" />
                <Skeleton variant="text" />
              </Grid>
              <Grid item sm={4}>
                <Skeleton variant="text" />
                <Skeleton variant="text" />
              </Grid>
            </Grid>
            <Grid item container spacing={2}>
              <Grid item container>
                <Grid item sm={4}>
                  <Skeleton variant="text" />
                </Grid>
                <Grid item sm={12}>
                  <Skeleton variant="text" />
                </Grid>
              </Grid>
              <Grid item container>
                <Grid item sm={4}>
                  <Skeleton variant="text" />
                </Grid>
                <Grid item sm={12}>
                  <Skeleton variant="text" />
                  <Skeleton variant="text" />
                </Grid>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      )}

      <Grid id="addEntity" container style={{ margin: "0 0 20px 0" }}>
        <Grid item lg={12} md={12} sm={12}>
          <Paper
            style={{
              marginTop: "10px",
              marginBottom: "15px",
              padding: "15px 35px 30px 35px",
              // display: "flex",
              flexDirection: "column",
              alignItems: "center",
              alignContent: "stretch",
              justifyContent: "space-between",
            }}
          >
            <Typography style={{ marginBottom: "10px" }}>Add Entity</Typography>
            <Autocomplete
              autoComplete
              autoSelect={true}
              name="Business Search"
              // {...props}
              value=""
              noOptionsText="Type to start search"
              loadingText="Searching..."
              defaultValue=""
              clearOnBlur={true}
              blurOnSelect={true}
              onChange={(event, newValue, reason) => {
                if (reason === "select-option") {
                  if (newValue) {
                    setBusinessSearchValue(
                      newValue?.Name || newValue?.entityName
                    );

                    return;
                  }

                  setBusinessSearchValue("");
                  setBusinessSearchInputText("");
                }

                if (reason === "blur") {
                  setBusinessSearchInputText("");
                  handleEntityDetails(newValue);
                }
              }}
              filterOptions={(x) => x}
              onInputChange={(event, newInputValue) => {
                setBusinessSearchInputText(newInputValue);
                if (newInputValue === "") setEntityOptions([]);
              }}
              getOptionLabel={(option) => {
                if (option.EntityName) {
                  return option.EntityName
                    ? `${option.EntityName} ${readableABNString(option.Abn)}`
                    : "";
                }

                return option.Name
                  ? `${option.Name} ${readableABNString(option.Abn)}`
                  : "";
              }}
              // inputValue={businessSearchInputText}
              options={entityOptions}
              loading={loadingBusinessSearch}
              renderInput={(params) => (
                <TextField
                  variant="outlined"
                  {...params}
                  // label="Asynchronous"
                  label="Search business"
                  value=""
                  placeholder="Search with ABN or business name"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingBusinessSearch ? <CircularProgress /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Paper>
        </Grid>
      </Grid>
    </>
  );
}
